import { Link } from "wouter";
import { MarketingNav, MarketingFooter } from "./layout";

const SECTIONS = [
  {
    id: "pipeline",
    eyebrow: "PIPELINE",
    title: "From inbound noise to booked engagement.",
    body: "Pull leads from Google Places, your own list, or a screenshot of someone's Instagram. AI Enrich and Apollo fill the gaps. The AI Lead Catcher acts as a channel-agnostic first responder, so nothing rots in an inbox.",
    highlight: { symbol: "⚡", text: "AI Lead Catcher engages & qualifies inbound leads before a rep ever touches them" },
    bullets: [
      ["Lead → client pipeline with full activity history", "AI Search, AI Enrich, screenshot-to-lead import"],
      ["Unified inbox: Gmail, Twilio SMS, public forms", "Automatic lead scoring & rep routing"],
    ],
    mockUI: null,
  },
  {
    id: "the-schedule",
    eyebrow: "THE SCHEDULE",
    title: "One source of truth for every engagement.",
    body: "Spreadsheet, calendar, list, and tour views over the same data. Multi-show, multi-talent booking with double-booking guards. Routing-sheet PDFs for tours. A public event-request page that drops straight into your pipeline.",
    highlight: null,
    bullets: [
      ["Master schedule with Google Calendar sync", "Tour planning with route optimization"],
      ["Native e-signature with SHA-256 audit hashes", "Double-booking guards across all talent"],
    ],
    mockUI: "schedule",
  },
  {
    id: "money",
    eyebrow: "MONEY",
    title: "Get paid the way real agencies get paid.",
    body: "Invoices, deposits, late fees, talent commission splits, and a billing audit that reconciles itself. Stripe Connect + PayPal. Money is stored in bigint cents — no floating-point surprises at tax time.",
    highlight: null,
    bullets: [
      ["Stripe Connect + PayPal, 0% platform fee on paid tiers", "Executive reports + lead-source attribution"],
      ["Public payment links & white-label portal", "Late-fee scheduler & self-reconciling billing audit"],
    ],
    mockUI: "invoice",
  },
  {
    id: "conversations",
    eyebrow: "CONVERSATIONS",
    title: "Email and SMS that don't feel like a SaaS shouted at them.",
    body: "Drip campaigns that pause the moment someone books. AI Reply Management drafts in your tone and length defaults. Inbound hard-bounces auto-suppress, so you stop blasting dead addresses.",
    highlight: { symbol: "☎", text: "AI Outbound Caller places calls in your own cloned voice (Agency Pro)" },
    bullets: [
      ["Gmail OAuth, drip campaigns, bulk sends, unified inbox", "Twilio SMS + browser dialer with consent prompts"],
    ],
    mockUI: "caller",
  },
  {
    id: "trust",
    eyebrow: "TRUST",
    title: "Built so your CFO doesn't have to ask.",
    body: "Per-workspace KEK in Cloud KMS. Row-level security. User-facing MFA, passkeys, IP allow-listing, step-up MFA gate for critical actions. Encrypted backups with scheduled pruning and monthly restore tests.",
    highlight: null,
    bullets: [
      ["Workspace data isolation + role-based permissions", "White-label custom domain, wordmark, From name"],
      ["One-click ZIP export — your data, on demand", "Envelope encryption, honeytokens, impossible-travel detection"],
    ],
    mockUI: null,
  },
];

function ScheduleMockUI() {
  const rows = [
    { date: "Nov 12", name: "Midwest Tour — Night 1", venue: "Chicago, IL", status: "Confirmed", statusColor: "text-[hsl(var(--green))]" },
    { date: "Nov 14", name: "Midwest Tour — Night 3", venue: "Vertex Theatre", status: "Routing", statusColor: "text-[hsl(var(--graphite))]" },
    { date: "Nov 15", name: "Midwest Tour — Night 4", venue: "Minneapolis, MN", status: "Hold", statusColor: "text-[hsl(var(--graphite))]" },
    { date: "Nov 18", name: "Corporate — Vimwell Q4", venue: "Hyatt Regency", status: "Confirmed", statusColor: "text-[hsl(var(--green))]" },
  ];
  return (
    <div className="mt-8 rounded-xl border border-[hsl(var(--fog))] overflow-hidden" data-testid="mock-ui-schedule">
      <div className="px-4 py-2.5 border-b border-[hsl(var(--fog))] bg-[hsl(var(--paper-dim,var(--paper)))]">
        <span className="text-[11px] font-mono text-[hsl(var(--graphite))] tracking-[0.06em]">
          clarik.io / master schedule · tour view
        </span>
      </div>
      <div className="divide-y divide-[hsl(var(--fog))]">
        {rows.map((row) => (
          <div key={row.date} className="grid grid-cols-[56px_1fr_auto_80px] items-center gap-4 px-4 py-3 text-[13px]">
            <span className="font-mono text-[hsl(var(--graphite))] text-[11px]">{row.date}</span>
            <span className="font-medium truncate">{row.name}</span>
            <span className="text-[hsl(var(--graphite))] text-right hidden sm:block">{row.venue}</span>
            <span className={`text-right text-[12px] font-medium ${row.statusColor}`}>{row.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function InvoiceMockUI() {
  return (
    <div className="mt-8 rounded-xl border border-[hsl(var(--fog))] overflow-hidden" data-testid="mock-ui-invoice">
      <div className="px-4 py-2.5 border-b border-[hsl(var(--fog))] bg-[hsl(var(--paper-dim,var(--paper)))]">
        <span className="text-[11px] font-mono text-[hsl(var(--graphite))] tracking-[0.06em]">
          clarik.io / invoices · INV-2041
        </span>
      </div>
      <div className="p-5 space-y-1">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="font-medium text-[15px]">Vimwell Q4 — Corporate Event</p>
            <p className="text-[12px] text-[hsl(var(--graphite))] mt-0.5">INV-2041 · Due Nov 30</p>
          </div>
          <span className="text-[12px] font-medium text-[hsl(var(--green))] bg-[hsl(var(--green))]/10 px-2.5 py-1 rounded-full">
            Paid
          </span>
        </div>
        {[
          { label: "Performance fee", amount: "$6,000.00" },
          { label: "Travel & lodging", amount: "$1,250.00" },
          { label: "Deposit received", amount: "−$2,000.00" },
        ].map((line) => (
          <div key={line.label} className="flex justify-between text-[13px] py-1.5 border-b border-[hsl(var(--fog))] last:border-0">
            <span className="text-[hsl(var(--ink-soft))]">{line.label}</span>
            <span className="font-medium tabular-nums">{line.amount}</span>
          </div>
        ))}
        <div className="flex justify-between text-[14px] pt-3 font-medium">
          <span>Balance paid via Stripe</span>
          <span className="text-[hsl(var(--green))] tabular-nums">$5,250.00</span>
        </div>
      </div>
    </div>
  );
}

function CallerMockUI() {
  return (
    <div className="mt-8 rounded-xl border border-[hsl(var(--fog))] overflow-hidden" data-testid="mock-ui-caller">
      <div className="px-4 py-2.5 border-b border-[hsl(var(--fog))] bg-[hsl(var(--paper-dim,var(--paper)))]">
        <span className="text-[11px] font-mono text-[hsl(var(--graphite))] tracking-[0.06em]">
          clarik.io / ai outbound caller
        </span>
      </div>
      <div className="p-6 flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-[hsl(var(--green))]/10 flex items-center justify-center flex-shrink-0">
          <span className="text-[18px]">☎</span>
        </div>
        <div>
          <p className="font-medium text-[14px]">Calling Riverside Hall — booking follow-up</p>
          <p className="text-[12px] text-[hsl(var(--graphite))] mt-0.5">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[hsl(var(--green))] mr-1.5 translate-y-[-1px]" />
            Live · your cloned voice · 00:42
          </p>
        </div>
      </div>
    </div>
  );
}

export default function MarketingFeatures() {
  return (
    <div
      className="min-h-screen flex flex-col bg-[hsl(var(--paper))] text-[hsl(var(--ink))]"
      data-testid="page-marketing-features"
    >
      <MarketingNav />

      <main className="flex-1">
        {/* Hero */}
        <section className="text-center pt-20 pb-16 px-6" data-testid="section-product-hero">
          <p className="text-[11px] font-mono tracking-[0.12em] uppercase text-[hsl(var(--graphite))] mb-6">
            THE PRODUCT
          </p>
          <h1 className="text-[36px] md:text-[64px] font-medium leading-[1.02] tracking-[-0.04em] mb-6 max-w-3xl mx-auto">
            Built by someone who actually books.
          </h1>
          <p className="text-[17px] md:text-[20px] text-[hsl(var(--ink-soft))] max-w-2xl mx-auto mb-10 leading-[1.55]">
            The whole workflow — leads, engagements, contracts, money,
            conversations, and reports — in one workspace. Not eight tabs and a prayer.
          </p>
          <Link
            href="/beta"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-[hsl(var(--green))] text-[hsl(var(--paper))] rounded-full text-[15px] font-medium hover:opacity-90 transition-opacity"
            data-testid="button-product-hero-cta"
          >
            Request access →
          </Link>
          <p className="mt-4 text-[12px] text-[hsl(var(--graphite))] flex flex-wrap items-center justify-center gap-4">
            <span>4 months free, then $50/mo for life.</span>
            <span className="w-1 h-1 rounded-full bg-[hsl(var(--fog))] hidden sm:block" />
            <span>Only 10 founding spots per business type.</span>
          </p>
        </section>

        {/* Feature sections */}
        <div className="max-w-[900px] mx-auto px-6 md:px-12 space-y-24 pb-24">
          {SECTIONS.map((s) => (
            <section key={s.id} data-testid={`section-feature-${s.id}`}>
              <p className="text-[11px] font-mono tracking-[0.1em] uppercase text-[hsl(var(--graphite))] mb-5">
                {s.eyebrow}
              </p>
              <h2 className="text-[26px] md:text-[34px] font-medium leading-[1.1] tracking-[-0.025em] mb-4 max-w-xl">
                {s.title}
              </h2>
              <p className="text-[16px] leading-[1.65] text-[hsl(var(--ink-soft))] max-w-2xl mb-6">
                {s.body}
              </p>

              {s.highlight && (
                <div className="flex items-start gap-2.5 mb-5 text-[14px] text-[hsl(var(--ink-soft))]">
                  <span className="flex-shrink-0">{s.highlight.symbol}</span>
                  <span>{s.highlight.text}</span>
                </div>
              )}

              <div className="space-y-2">
                {s.bullets.map((row, ri) => (
                  <div key={ri} className="grid sm:grid-cols-2 gap-2">
                    {row.map((bullet) => (
                      <div key={bullet} className="flex items-start gap-2.5 text-[14px] text-[hsl(var(--ink-soft))]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--fog))] flex-shrink-0 mt-[6px]" />
                        <span>{bullet}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {s.mockUI === "schedule" && <ScheduleMockUI />}
              {s.mockUI === "invoice" && <InvoiceMockUI />}
              {s.mockUI === "caller" && <CallerMockUI />}
            </section>
          ))}
        </div>

        {/* Closing CTA */}
        <section
          className="text-center py-20 px-6 border-t border-[hsl(var(--fog))]"
          data-testid="section-product-closing-cta"
        >
          <h2 className="text-[28px] md:text-[42px] font-medium leading-[1.05] tracking-[-0.03em] mb-6 max-w-xl mx-auto">
            See the whole workflow in one place.
          </h2>
          <p className="text-[13px] text-[hsl(var(--graphite))] mb-8 flex flex-wrap items-center justify-center gap-4">
            <span>Apply to be a Beta Tester - Get our $129/mn plan 4 months free, then $50/mo for life as a thank you.</span>
            <span className="w-1 h-1 rounded-full bg-[hsl(var(--fog))] hidden sm:block" />
            <span>Only 10 spots per business type.</span>
          </p>
          <Link
            href="/beta"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-[hsl(var(--green))] text-[hsl(var(--paper))] rounded-full text-[15px] font-medium hover:opacity-90 transition-opacity"
            data-testid="button-product-closing-cta"
          >
            Request access →
          </Link>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
